import React, { useEffect, useState } from "react";
import { EventBus } from "@engine/EventBus.js";
import { LootPileStore } from "@store/LootPileStore.js";
import { InventoryStore } from "@store/InventoryStore.js";
import { PartyStore } from "@store/PartyStore.js";
import { lootAllToStrongestCapacity } from "@engine/inventory/lootAll.js";
import { takeItemById } from "@engine/inventory/LootPile.js";
import { remainingCapacity } from "@engine/inventory/CharacterInventory.js";
import type { LootPile } from "@engine/inventory/LootPile.js";
import { GOLD_ITEM_ID } from "@engine/inventory/goldItem.js";
import { WorldStore } from "@store/WorldStore.js";
import { ItemIcon } from "./ItemIcon.js";

/**
 * Remove the pile's persistent record (I18) and make sure exactly one
 * `world:item_drop_picked_up` fires — WorldStore emits it when the pile
 * was tracked there; legacy piles that predate persistence fall back to
 * a direct emission so the billboard still despawns.
 */
function announcePilePickedUp(pileId: string): void {
  if (!WorldStore.removeFloorDrop(pileId)) {
    EventBus.emit("world:item_drop_picked_up", { dropId: pileId });
  }
}

/**
 * U26 — Floor loot pile pickup modal.
 *
 * Opened by `ui:loot_pile_open` when the player clicks a loot-pile sprite.
 * Shows the pile's item stacks; "Take All" routes everything to the party
 * member with the most remaining carry capacity. Refused items stay on the
 * pile. When the pile is empty the modal closes; main.tsx removes the sprite
 * on world:item_drop_picked_up.
 */
export function LootPileModal() {
  const [pileId, setPileId] = useState<string | null>(null);
  const [pile, setPile] = useState<LootPile | null>(null);

  useEffect(() => {
    const off = EventBus.on("ui:loot_pile_open", ({ pileId: id }) => {
      const found = LootPileStore.get(id);
      if (!found || found.stacks.length === 0) return;
      setPileId(id);
      setPile(found);
    });
    return off;
  }, []);

  if (!pileId || !pile) return null;

  const close = () => {
    setPileId(null);
    setPile(null);
  };

  const takeAll = () => {
    const members = PartyStore.getMembers();
    // I19: gold goes to the shared purse, never into a carry inventory.
    const goldQty = pile.stacks
      .filter((s) => s.item.id === GOLD_ITEM_ID)
      .reduce((sum, s) => sum + s.quantity, 0);
    if (goldQty > 0) {
      PartyStore.earnGold(goldQty);
      EventBus.emit("inventory:gold_changed", { gold: PartyStore.getGold(), delta: goldQty });
    }
    const carryable = { ...pile, stacks: pile.stacks.filter((s) => s.item.id !== GOLD_ITEM_ID) };
    const partyMembers = members.map((m, i) => ({
      characterId: m.instanceId,
      strengthScore: m.abilities.STR,
      inventory: InventoryStore.get(m.instanceId),
      order: i,
    }));
    const result = lootAllToStrongestCapacity(carryable, partyMembers);
    if (result.recipient) {
      InventoryStore.set(result.recipient, result.inventory);
    }
    // Announce what actually left the pile on the observer-only channel so
    // KeyringStore sees loot-all grants without a double inventory add
    // (the inventory write already happened via InventoryStore.set above).
    const remainingById = new Map(result.pile.stacks.map((s) => [s.item.id, s.quantity]));
    for (const stack of carryable.stacks) {
      const taken = stack.quantity - (remainingById.get(stack.item.id) ?? 0);
      if (taken > 0) {
        EventBus.emit("inventory:item_acquired", { itemId: stack.item.id, qty: taken });
      }
    }
    LootPileStore.update(result.pile);
    const remaining = LootPileStore.get(pileId);
    if (!remaining || remaining.stacks.length === 0) {
      announcePilePickedUp(pileId);
      close();
    } else {
      setPile({ ...remaining });
    }
  };

  const takeSingle = (itemId: string) => {
    const members = PartyStore.getMembers();
    if (members.length === 0) return;
    let bestId = members[0]!.instanceId;
    let bestCap = -Infinity;
    for (const m of members) {
      const cap = remainingCapacity(InventoryStore.get(m.instanceId), m.abilities.STR);
      if (cap > bestCap) { bestCap = cap; bestId = m.instanceId; }
    }
    void bestId; // InventoryStore subscription handles the routing
    const result = takeItemById(pile, itemId);
    if (!result) return;
    // Route to InventoryStore via give_item event
    EventBus.emit("inventory:give_item", { itemId, qty: result.stack.quantity });
    LootPileStore.update(result.pile);
    const remaining = LootPileStore.get(pileId);
    if (!remaining || remaining.stacks.length === 0) {
      announcePilePickedUp(pileId);
      close();
    } else {
      setPile({ ...remaining });
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        zIndex: 200,
      }}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#1a1a1a",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: 8,
          padding: 24,
          minWidth: 280,
          maxWidth: 400,
          fontFamily: "monospace",
        }}
      >
        <h3
          style={{
            margin: "0 0 12px",
            color: "white",
            fontSize: 14,
            letterSpacing: 2,
          }}
        >
          LOOT PILE
        </h3>
        <div style={{ maxHeight: 240, overflowY: "auto", marginBottom: 12 }}>
          {pile.stacks.map((stack) => (
            <div
              key={stack.item.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "4px 0",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                color: "#ddd",
                fontSize: 12,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                <ItemIcon itemId={stack.item.id} name={stack.item.name} size={20} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {stack.item.name}
                  {stack.quantity > 1 && (
                    <span style={{ color: "#888", marginLeft: 4 }}>×{stack.quantity}</span>
                  )}
                </span>
              </span>
              <button
                type="button"
                onClick={() => takeSingle(stack.item.id)}
                style={smallBtn}
              >
                Take
              </button>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={takeAll} style={primaryBtn}>
            Take All
          </button>
          <button type="button" onClick={close} style={secondaryBtn}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const smallBtn: React.CSSProperties = {
  padding: "2px 8px",
  fontSize: 10,
  fontFamily: "monospace",
  background: "#2a4060",
  color: "#c8e6ff",
  border: "1px solid rgba(100,160,220,0.4)",
  borderRadius: 3,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  padding: "6px 16px",
  fontFamily: "monospace",
  fontSize: 12,
  background: "#2a4060",
  color: "#c8e6ff",
  border: "1px solid rgba(100,160,220,0.4)",
  borderRadius: 4,
  cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
  padding: "6px 16px",
  fontFamily: "monospace",
  fontSize: 12,
  background: "rgba(0,0,0,0.4)",
  color: "#aaa",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 4,
  cursor: "pointer",
};
